/**
 * Timesheets & Payroll — /timesheets (#427 W3, spec §5 W3).
 *
 * The money-facing surface a real business runs payroll from. Per-staff
 * computed rows over a pay period (Colorado rules pack, server-side):
 * regular · overtime (winning trigger inspectable) · paid/unpaid breaks ·
 * declared tips · rest-period flags · gross. A manager attests flagged rest
 * periods and approves each timesheet; the page's single filled-primary is
 * Export CSV for the payroll provider.
 *
 * Route-gated on payroll:read:all (owner/manager/accountant) via
 * ROUTE_ACCESS_RULES['/timesheets'] + the server route-permission map.
 *
 * Design Philosophy principles served:
 *  - P1 (one glance, one truth): each staffer's hours + pay read at a glance;
 *    the approval count and the export gate come from the SAME row set.
 *  - P2 (next action obvious): Export CSV is the sole filled-primary; open
 *    punches never dead-end — they link to Punch records; no rate → a
 *    "Set a rate" affordance, never a silent blank.
 *  - P5 (never lie about data): a missing rate shows an honest empty + flag,
 *    NEVER a fake $0.00; a flagged-but-unconfirmed rest shows "$ at risk"
 *    until attested; a partial/at-risk export requires an explicit confirm.
 *  - P6 (tokens only): status via --ferro-* families, spacing on the 4px grid.
 *  - P7 (built for hands): per-staff cards (no horizontal scroll), ≥44px
 *    targets, no hover-only affordances.
 *
 * Five states: default · loading (skeleton) · empty (teaching copy + invite
 * action) · error (what happened + retry). "Stale" is not applicable —
 * timesheets recompute live from punches on every period change (there is no
 * cached mirror to age), so there is no honest "stale" surface to render.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { Modal, PageHeader } from '@ferro/ui';
import {
  ApiError,
  adminPayrollApi,
  adminTimesheetsApi,
  addIsoDays,
  exportPayrollCsv,
  humanizeApiError,
  todayIsoDate,
  type PayrollExportSummary,
  type TimesheetRowView,
} from '../lib/api.js';
import { useAuth } from '../contexts/AuthContext.js';
import { effectiveRolesOf, rolesHavePermission } from '../lib/rbac-matrix.js';
import {
  atRiskCents,
  flagLabel,
  formatUsdCents,
  minutesToHours,
  otBasisLabel,
} from '../lib/timesheet-format.js';
import { formatDayShort } from '../lib/schedule-dates.js';
import { TimesheetApprovalModal } from '../components/timesheets/TimesheetApprovalModal.js';
import { PayRatesModal } from '../components/timesheets/PayRatesModal.js';
// Sawyer 15 workweek-hours-ot (E2)
import { WorkweekHoursPanel, PANEL_ID } from '../components/timesheets/WorkweekHoursPanel.js';
import { OvertimeWatchBanner } from '../components/timesheets/OvertimeWatchBanner.js';
import { useWorkweekHours } from '../components/timesheets/useWorkweekHours.js';
import { rolesCanAccessPath } from '../lib/route-permissions.js';

/** Sawyer 15 (E2) — which payroll file an export produces. */
type ExportFormat = 'period' | 'workweek';
type ExportOpts = { format: ExportFormat; confirmUnreviewed: boolean };

type TimesheetsProps = { config: ClientConfig };

type ListState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rows: TimesheetRowView[] };

type ExportState =
  | { kind: 'idle' }
  | { kind: 'exporting' }
  | { kind: 'error'; message: string }
  | {
      kind: 'done';
      fileName: string;
      exportId: string | null;
      checksum: string | null;
    };

/** The current pay period — a workweek-complete Sunday→Saturday window by
 *  default (spec D8 workweekStart=sunday). The manager can widen it (e.g. to
 *  a biweekly period) with the date inputs. */
function currentWeekPeriod(): { start: string; end: string } {
  const today = todayIsoDate();
  const weekday = new Date(`${today}T12:00:00`).getDay(); // 0 = Sunday
  const start = addIsoDays(today, -weekday);
  return { start, end: addIsoDays(start, 6) };
}

function periodLengthDays(start: string, end: string): number {
  const ms =
    new Date(`${end}T12:00:00`).getTime() - new Date(`${start}T12:00:00`).getTime();
  return Math.round(ms / (24 * 60 * 60 * 1000)) + 1;
}

function fmtPeriodLabel(start: string, end: string): string {
  const s = new Date(`${start}T12:00:00`);
  const e = new Date(`${end}T12:00:00`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) {
    return `${start} – ${end}`;
  }
  const short: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  return `${s.toLocaleDateString('en-US', short)} – ${e.toLocaleDateString(
    'en-US',
    { ...short, year: 'numeric' },
  )}`;
}

export function Timesheets({ config }: TimesheetsProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { staff } = useAuth();
  const roles = effectiveRolesOf(staff);
  const canApprove = rolesHavePermission(roles, 'payroll.clock:write:all');
  const canEditRates = rolesHavePermission(roles, 'payroll.rates:write:all');
  // Sawyer 15 workweek-hours-ot (E2) — the This-week panel and the overtime
  // banner share ONE read (Principle 1). null week = the server's "this week".
  const canOpenPath = useCallback((path: string) => rolesCanAccessPath(roles, path), [roles]);
  const [workweekDate, setWorkweekDate] = useState<string | null>(null);
  const workweek = useWorkweekHours(apiUrl, workweekDate);
  const workweekData = workweek.state.kind === 'ready' ? workweek.state.data : null;
  // The banner is about the CURRENT week, whatever week the panel is showing.
  const currentWeek = useWorkweekHours(apiUrl, null, { enabled: workweekDate !== null });
  const bannerData =
    workweekDate === null
      ? workweekData
      : currentWeek.state.kind === 'ready'
        ? currentWeek.state.data
        : null;
  const scrollToPanelOnLoad = useCallback(() => {
    if (typeof window === 'undefined' || window.location.hash !== `#${PANEL_ID}`) return;
    document.getElementById(PANEL_ID)?.scrollIntoView({ block: 'start' });
  }, []);

  const [period, setPeriod] = useState(() => currentWeekPeriod());
  const [listState, setListState] = useState<ListState>({ kind: 'loading' });

  // Approval is tracked in-session: after an in-app approval we KNOW the row
  // is approved. On a fresh load we cannot (see the API-contract note below),
  // so the export gate treats not-known-approved as "needs confirm" — the
  // safe direction (never silently export an unapproved period).
  const [approvedIds, setApprovedIds] = useState<ReadonlySet<string>>(new Set());

  const [approvalRow, setApprovalRow] = useState<TimesheetRowView | null>(null);
  const [ratesFor, setRatesFor] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const [exportState, setExportState] = useState<ExportState>({ kind: 'idle' });
  // Sawyer 15 (E2) — the confirm dialog knows which file it is confirming.
  const [confirmExport, setConfirmExport] = useState<ExportFormat | null>(null);
  // R16 — "Try again" replays the last export exactly (format + confirm).
  const lastExportOpts = useRef<ExportOpts>({ format: 'period', confirmUnreviewed: false });

  const [history, setHistory] = useState<PayrollExportSummary[] | 'loading' | 'error'>(
    'loading',
  );

  const loadRows = useCallback(async (): Promise<void> => {
    setListState({ kind: 'loading' });
    const result = await adminTimesheetsApi.list(apiUrl, period.start, period.end);
    if (result.error !== undefined || !result.data) {
      setListState({
        kind: 'error',
        message: humanizeApiError(result.error ?? null),
      });
      return;
    }
    setListState({ kind: 'ready', rows: result.data.rows });
  }, [apiUrl, period.start, period.end]);

  const loadHistory = useCallback(async (): Promise<void> => {
    setHistory('loading');
    const result = await adminPayrollApi.listExports(apiUrl);
    if (result.error !== undefined || !result.data) {
      setHistory('error');
      return;
    }
    setHistory(result.data.exports);
  }, [apiUrl]);

  // A period change is a fresh computation — reset session approvals so the
  // export gate never carries a prior period's approvals into a new one.
  useEffect(() => {
    setApprovedIds(new Set());
    setExportState({ kind: 'idle' });
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const rows = listState.kind === 'ready' ? listState.rows : [];

  /** A row is "confirmed" in the UI when it was approved in this session OR
   *  a prior approval left attested-missed rests on it (a positive signal an
   *  approval exists). See the API-contract note in the report. */
  // Sawyer 15 (E2) — the server's `approved` flag (a saved approval) is read,
  // so approvals survive a reload; in-session approvals still count at once.
  const isConfirmed = useCallback(
    (row: TimesheetRowView): boolean =>
      row.approved === true ||
      approvedIds.has(row.staff_user_id) ||
      row.rest_periods_attested_missed > 0,
    [approvedIds],
  );

  const openEntryRows = rows.filter((r) => r.flags.includes('open_entry'));
  const unapprovedRows = rows.filter((r) => !isConfirmed(r));
  const approvedCount = rows.length - unapprovedRows.length;
  const fullyApproved =
    rows.length > 0 && unapprovedRows.length === 0 && openEntryRows.length === 0;

  const handleApproved = (updated: TimesheetRowView): void => {
    setApprovedIds((prev) => new Set(prev).add(updated.staff_user_id));
    setListState((curr) =>
      curr.kind === 'ready'
        ? {
            kind: 'ready',
            rows: curr.rows.map((r) =>
              r.staff_user_id === updated.staff_user_id ? updated : r,
            ),
          }
        : curr,
    );
    setApprovalRow(null);
  };

  const doExport = useCallback(async (opts: ExportOpts): Promise<void> => {
    setConfirmExport(null);
    lastExportOpts.current = opts;
    setExportState({ kind: 'exporting' });
    try {
      // Sawyer 15 (E2) — the server refuses an unreviewed period unless the
      // manager confirmed ("Export anyway" sends confirm_unreviewed).
      const result = await exportPayrollCsv(apiUrl, period.start, period.end, {
        format: opts.format,
        confirmUnreviewed: opts.confirmUnreviewed,
      });
      // Trigger the browser download from the streamed CSV blob.
      if (typeof document !== 'undefined') {
        const url = URL.createObjectURL(result.blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = result.fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      setExportState({
        kind: 'done',
        fileName: result.fileName,
        exportId: result.exportId,
        checksum: result.checksumSha256,
      });
      void loadHistory();
    } catch (err) {
      // Someone un-approved meanwhile (or an API caller raced us): the server
      // says the period needs review → the same confirm dialog, not a raw error.
      if (err instanceof ApiError && err.code === 'export_needs_review') {
        setExportState({ kind: 'idle' });
        setConfirmExport(opts.format);
        return;
      }
      setExportState({
        kind: 'error',
        message:
          err instanceof Error
            ? err.message
            : 'The payroll export didn’t finish. Try again.',
      });
    }
  }, [apiUrl, period.start, period.end, loadHistory]);

  const shiftPeriod = (dir: -1 | 1): void => {
    const len = periodLengthDays(period.start, period.end);
    setPeriod((p) => ({
      start: addIsoDays(p.start, dir * len),
      end: addIsoDays(p.end, dir * len),
    }));
  };

  const toggleExpand = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const periodValid = period.end >= period.start;

  return (
    <section className="ferro-admin-page" data-testid="timesheets-page">
      <PageHeader
        title="Timesheets & payroll"
        subtitle={fmtPeriodLabel(period.start, period.end)}
        actions={
          // The page's single filled-primary. Enabled only when the period is
          // fully approved with no open punches (spec §5 W3); the
          // export-with-warnings path is an explicit, separate affordance
          // below so a partial export is never one careless click.
          <div className="ferro-ts-export-actions">
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={() => void doExport({ format: 'period', confirmUnreviewed: false })}
              disabled={
                listState.kind !== 'ready' ||
                rows.length === 0 ||
                exportState.kind === 'exporting' ||
                !periodValid ||
                !fullyApproved
              }
              aria-busy={exportState.kind === 'exporting'}
              data-testid="export-csv"
            >
              {exportState.kind === 'exporting' ? 'Exporting…' : 'Export CSV'}
            </button>
            {/* Sawyer 15 (E2) — the accountant's week-by-week file. Outline,
                never a second primary. On an unreviewed period it opens the
                same confirm dialog instead of exporting. */}
            <button
              type="button"
              className="ferro-btn ferro-btn--outline"
              onClick={() =>
                fullyApproved
                  ? void doExport({ format: 'workweek', confirmUnreviewed: false })
                  : setConfirmExport('workweek')
              }
              disabled={
                listState.kind !== 'ready' ||
                rows.length === 0 ||
                exportState.kind === 'exporting' ||
                !periodValid
              }
              data-testid="export-workweek"
            >
              Week-by-week CSV
            </button>
          </div>
        }
      />

      {/* Sawyer 15 workweek-hours-ot (E2) — overtime watch (current week) and
          this week's hours, above the pay-period toolbar. */}
      <OvertimeWatchBanner data={bannerData} />
      <WorkweekHoursPanel
        apiUrl={apiUrl}
        state={workweek.state}
        onRetry={workweek.reload}
        onPickWeek={setWorkweekDate}
        canEditThreshold={canEditRates}
        canOpen={canOpenPath}
        onThresholdSaved={() => {
          workweek.reload();
          if (workweekDate !== null) currentWeek.reload();
        }}
        onFirstReady={scrollToPanelOnLoad}
      />

      {/* Period selector — secondary controls (Export CSV is the primary). */}
      <div className="ferro-ts-toolbar" role="group" aria-label="Pay period">
        <button
          type="button"
          className="ferro-btn ferro-btn--sm ferro-btn--outline"
          onClick={() => shiftPeriod(-1)}
          aria-label="Previous period"
        >
          ← Prev
        </button>
        <label className="ferro-field ferro-ts-toolbar__date">
          <span className="ferro-field__label">Start</span>
          <input
            type="date"
            className="ferro-field__input"
            value={period.start}
            max={period.end}
            onChange={(e) =>
              setPeriod((p) => ({ ...p, start: e.target.value || p.start }))
            }
            data-testid="period-start"
          />
        </label>
        <label className="ferro-field ferro-ts-toolbar__date">
          <span className="ferro-field__label">End</span>
          <input
            type="date"
            className="ferro-field__input"
            value={period.end}
            min={period.start}
            onChange={(e) =>
              setPeriod((p) => ({ ...p, end: e.target.value || p.end }))
            }
            data-testid="period-end"
          />
        </label>
        <button
          type="button"
          className="ferro-btn ferro-btn--sm ferro-btn--outline"
          onClick={() => shiftPeriod(1)}
          aria-label="Next period"
        >
          Next →
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--sm ferro-btn--ghost"
          onClick={() => setPeriod(currentWeekPeriod())}
        >
          This week
        </button>
      </div>

      {/* Approval progress — the honest gate on a clean export (P1/P5). The
          filled-primary Export is disabled until this reads "all approved";
          exporting sooner is a deliberate, separate action. */}
      {listState.kind === 'ready' && rows.length > 0 ? (
        <div className="ferro-ts-approval-row">
          <p
            className={`ferro-ts-approval-note${
              fullyApproved ? ' ferro-ts-approval-note--ok' : ''
            }`}
            data-testid="approval-note"
          >
            {fullyApproved
              ? 'All timesheets approved — ready to export.'
              : `${approvedCount} of ${rows.length} approved${
                  openEntryRows.length > 0
                    ? ` · ${openEntryRows.length} with an open punch`
                    : ''
                } — approve each below, or export with open warnings.`}
          </p>
          {!fullyApproved ? (
            <button
              type="button"
              className="ferro-btn ferro-btn--sm ferro-btn--outline"
              onClick={() => setConfirmExport('period')}
              disabled={exportState.kind === 'exporting' || !periodValid}
              data-testid="export-with-warnings"
            >
              Export with open warnings
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Sawyer 15 (E2) — what the week-by-week file is (and is not). */}
      {listState.kind === 'ready' && rows.length > 0 ? (
        <p className="ferro-ts-caption" data-testid="workweek-export-caption">
          Week-by-week shows hours and overtime by Sunday-to-Saturday week.
          Missed-rest wages and tips are in the per-period CSV.
        </p>
      ) : null}

      {/* Export outcome — a persistent, inspectable record (never a toast
          alone): the frozen export id + checksum are the accountant's
          artifact (#135). */}
      {exportState.kind === 'done' ? (
        <div
          className="ferro-alert ferro-alert--success ferro-alert--dismissible"
          role="status"
          data-testid="export-done"
        >
          <span>
            Exported <strong>{exportState.fileName}</strong>.
            {exportState.exportId
              ? ` Frozen export ${exportState.exportId.slice(0, 8)}`
              : ''}
            {exportState.checksum
              ? ` · checksum ${exportState.checksum.slice(0, 12)}…`
              : ''}
          </span>
          <button
            type="button"
            className="ferro-alert__dismiss"
            onClick={() => setExportState({ kind: 'idle' })}
          >
            Dismiss
          </button>
        </div>
      ) : null}

      {exportState.kind === 'error' ? (
        <div className="ferro-alert ferro-alert--error ferro-alert--dismissible" role="alert" data-testid="export-error">
          <span>{exportState.message}</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline"
            onClick={() => void doExport(lastExportOpts.current)}
          >
            Try again
          </button>
        </div>
      ) : null}

      {/* ── Rows ─────────────────────────────────────────────────────── */}
      {listState.kind === 'loading' ? (
        <div className="ferro-skeleton-stack" data-testid="timesheets-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="ferro-skeleton ferro-skeleton--stat" />
          ))}
        </div>
      ) : listState.kind === 'error' ? (
        <div className="ferro-alert ferro-alert--error" role="alert">
          <span>{listState.message}</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline"
            onClick={() => void loadRows()}
          >
            Try again
          </button>
        </div>
      ) : rows.length === 0 ? (
        <div className="ferro-ts-empty" data-testid="timesheets-empty">
          <p className="ferro-ts-empty__lead">No staff to show for this period.</p>
          <p className="ferro-ts-empty__hint">
            Timesheets appear once you have active staff clocking in. Invite
            your team to get started.
          </p>
          <Link className="ferro-btn ferro-btn--outline" to="/staff">
            Go to Staff
          </Link>
        </div>
      ) : (
        <div className="ferro-ts-rows">
          {rows.map((row) => (
            <TimesheetRowCard
              key={row.staff_user_id}
              row={row}
              confirmed={isConfirmed(row)}
              canApprove={canApprove}
              canEditRates={canEditRates}
              expanded={expanded.has(row.staff_user_id)}
              onToggleExpand={() => toggleExpand(row.staff_user_id)}
              onApprove={() => setApprovalRow(row)}
              onRates={() =>
                setRatesFor({ id: row.staff_user_id, name: row.employee_name })
              }
            />
          ))}
        </div>
      )}

      {/* Cash-tip provenance — an inline caption, not a hover tooltip (banned
          on touch surfaces): declared cash tips show here now; card tips
          (#360) merge in later. */}
      {listState.kind === 'ready' && rows.length > 0 ? (
        <p className="ferro-ts-caption" data-testid="tips-caption">
          Tips shown are cash tips groomers declare at clock-out. Card tips
          (coming soon) will merge in as a second source.
        </p>
      ) : null}

      {/* ── Export history (accountant artifact, #135) ──────────────── */}
      <ExportHistory history={history} onRetry={() => void loadHistory()} />

      {approvalRow ? (
        <TimesheetApprovalModal
          apiUrl={apiUrl}
          row={approvalRow}
          onClose={() => setApprovalRow(null)}
          onApproved={handleApproved}
        />
      ) : null}

      {ratesFor ? (
        <PayRatesModal
          apiUrl={apiUrl}
          staffUserId={ratesFor.id}
          staffName={ratesFor.name}
          canEdit={canEditRates}
          onClose={() => setRatesFor(null)}
          onSaved={() => void loadRows()}
        />
      ) : null}

      {confirmExport !== null ? (
        <ExportConfirmDialog
          format={confirmExport}
          openEntryRows={openEntryRows}
          unapprovedRows={unapprovedRows}
          onCancel={() => setConfirmExport(null)}
          onConfirm={() => void doExport({ format: confirmExport, confirmUnreviewed: true })}
        />
      ) : null}
    </section>
  );
}

// ── Per-staff row card ─────────────────────────────────────────────────

interface RowCardProps {
  row: TimesheetRowView;
  confirmed: boolean;
  canApprove: boolean;
  canEditRates: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  onApprove: () => void;
  onRates: () => void;
}

function TimesheetRowCard({
  row,
  confirmed,
  canApprove,
  canEditRates,
  expanded,
  onToggleExpand,
  onApprove,
  onRates,
}: RowCardProps): JSX.Element {
  const noRate = row.flags.includes('no_rate_set') || row.hourly_rate_cents === null;
  const hasOpenEntry = row.flags.includes('open_entry');
  const atRisk = atRiskCents(row.rest_periods_flagged, row.hourly_rate_cents);

  return (
    <article
      className="ferro-ts-row"
      data-testid={`timesheet-row-${row.staff_user_id}`}
    >
      <header className="ferro-ts-row__head">
        <div className="ferro-ts-row__id">
          <span className="ferro-ts-row__name">{row.employee_name}</span>
          <span className="ferro-ts-row__email">{row.email}</span>
        </div>
        <div className="ferro-ts-row__badges">
          {confirmed ? (
            <span className="ferro-ts-chip ferro-ts-chip--ok" data-testid="approved-badge">
              Approved
            </span>
          ) : null}
          {row.flags.map((f) => (
            <span
              key={f}
              className={`ferro-ts-chip ferro-ts-chip--${
                f === 'open_entry' ? 'danger' : 'warn'
              }`}
              data-testid={`flag-${f}`}
            >
              {flagLabel(f)}
            </span>
          ))}
          {row.rate_changed_in_period ? (
            <span className="ferro-ts-chip ferro-ts-chip--warn">
              Rate changed mid-period
            </span>
          ) : null}
        </div>
      </header>

      <dl className="ferro-ts-figures">
        <div className="ferro-ts-figure">
          <dt>Regular</dt>
          <dd className="ferro-ts-tnum">{minutesToHours(row.regular_minutes)}h</dd>
        </div>
        <div className="ferro-ts-figure">
          <dt>Overtime</dt>
          <dd className="ferro-ts-tnum">
            {minutesToHours(row.overtime_minutes)}h
            {row.overtime_minutes > 0 ? (
              <span
                className="ferro-ts-chip ferro-ts-chip--info"
                title={otBasisLabel(row.ot_basis)}
                data-testid="ot-basis"
              >
                {otBasisLabel(row.ot_basis)}
              </span>
            ) : null}
          </dd>
        </div>
        <div className="ferro-ts-figure">
          <dt>Breaks</dt>
          <dd className="ferro-ts-tnum">
            {minutesToHours(row.paid_break_minutes)}h paid ·{' '}
            {minutesToHours(row.unpaid_break_minutes)}h unpaid
          </dd>
        </div>
        <div className="ferro-ts-figure">
          <dt>Tips (cash)</dt>
          <dd className="ferro-ts-tnum">{formatUsdCents(row.declared_tips_cents)}</dd>
        </div>
        <div className="ferro-ts-figure">
          <dt>Rate</dt>
          <dd>
            {noRate ? (
              <button
                type="button"
                className="ferro-btn ferro-btn--sm ferro-btn--outline"
                onClick={onRates}
                data-testid="set-rate"
              >
                {canEditRates ? 'Set a rate' : 'No rate set'}
              </button>
            ) : (
              <button
                type="button"
                className="ferro-ts-rate-link ferro-ts-tnum"
                onClick={onRates}
                data-testid="rate-value"
              >
                {formatUsdCents(row.hourly_rate_cents as number)}/hr
              </button>
            )}
          </dd>
        </div>
        <div className="ferro-ts-figure">
          <dt>Gross pay</dt>
          <dd className="ferro-ts-tnum" data-testid="gross-total">
            {row.gross_total_cents === null ? (
              <span className="ferro-ts-empty-money" data-testid="gross-empty">
                — <span className="ferro-ts-empty-money__why">no rate set</span>
              </span>
            ) : (
              formatUsdCents(row.gross_total_cents)
            )}
          </dd>
        </div>
      </dl>

      {/* Open punch blocks a clean approval — never a dead end (P2). */}
      {hasOpenEntry ? (
        <div
          className="ferro-ts-note ferro-ts-note--danger"
          role="alert"
          data-testid="open-entry-note"
        >
          <span>
            An open punch is still running — someone is clocked in without a
            clock-out. Fix it before approving.
          </span>
          <Link className="ferro-btn ferro-btn--sm ferro-btn--outline" to="/timeclock/manage">
            Fix in Punch records
          </Link>
        </div>
      ) : null}

      {/* Rest-period honesty line (P5): unconfirmed → $ at risk; confirmed →
          the owed result. */}
      {row.rest_periods_flagged > 0 && !confirmed ? (
        <div
          className="ferro-ts-note ferro-ts-note--warn"
          data-testid="rest-at-risk"
        >
          {row.rest_periods_flagged} rest{' '}
          {row.rest_periods_flagged === 1 ? 'period' : 'periods'} unconfirmed —{' '}
          {atRisk === null ? (
            <strong>amount unknown until a rate is set</strong>
          ) : (
            <>
              up to <strong className="ferro-ts-tnum">{formatUsdCents(atRisk)}</strong> at
              risk
            </>
          )}
          .
        </div>
      ) : confirmed && row.rest_periods_attested_missed > 0 ? (
        <div className="ferro-ts-note" data-testid="rest-owed">
          {row.rest_periods_attested_missed} missed rest{' '}
          {row.rest_periods_attested_missed === 1 ? 'period' : 'periods'} confirmed —{' '}
          <strong className="ferro-ts-tnum">
            {formatUsdCents(row.missed_rest_wages_owed_cents)}
          </strong>{' '}
          owed.
        </div>
      ) : null}

      {/* Expanded daily rest-flag detail (the per-day breakdown the row
          already carries; punch-level detail lives in Punch records). */}
      {expanded ? (
        <div className="ferro-ts-detail" data-testid="row-detail">
          {row.rest_flags.length === 0 ? (
            <p className="ferro-muted">
              No rest-period gaps this period. Punch-by-punch detail is on{' '}
              <Link to="/timeclock/manage">Punch records</Link>.
            </p>
          ) : (
            <ul className="ferro-ts-detail__list">
              {row.rest_flags.map((f) => (
                <li key={f.date} className="ferro-ts-detail__item ferro-ts-tnum">
                  {formatDayShort(f.date)}: owed {f.owed}, punched {f.punched},
                  missing {f.missing}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <footer className="ferro-ts-row__actions">
        <button
          type="button"
          className="ferro-btn ferro-btn--sm ferro-btn--ghost"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          data-testid="toggle-detail"
        >
          {expanded ? 'Hide detail' : 'Detail'}
        </button>
        {canApprove ? (
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline"
            onClick={onApprove}
            data-testid="approve-open"
          >
            {confirmed ? 'Re-approve' : 'Approve'}
          </button>
        ) : null}
      </footer>
    </article>
  );
}

// ── Export-with-warnings confirm dialog ────────────────────────────────

interface ExportConfirmProps {
  /** Sawyer 15 (E2) — which file "Export anyway" produces. */
  format: ExportFormat;
  openEntryRows: TimesheetRowView[];
  unapprovedRows: TimesheetRowView[];
  onCancel: () => void;
  onConfirm: () => void;
}

function ExportConfirmDialog({
  format,
  openEntryRows,
  unapprovedRows,
  onCancel,
  onConfirm,
}: ExportConfirmProps): JSX.Element {
  const names = (rowsIn: TimesheetRowView[]): string =>
    rowsIn.map((r) => r.employee_name).join(', ');
  return (
    <Modal isOpen onClose={onCancel} title="Export before everything's confirmed?" size="md">
      <p className="ferro-ts-modal__lead">
        Some timesheets in this period aren’t fully squared away. You can still
        export the {format === 'workweek' ? 'week-by-week' : 'per-period'} file, but check these first:
      </p>
      <ul className="ferro-ts-warnlist" data-testid="export-warnings">
        {openEntryRows.length > 0 ? (
          <li>
            <strong>{openEntryRows.length} with an open punch:</strong>{' '}
            {names(openEntryRows)} — their hours may be incomplete.
          </li>
        ) : null}
        {unapprovedRows.length > 0 ? (
          <li>
            <strong>{unapprovedRows.length} not approved yet:</strong>{' '}
            {names(unapprovedRows)}.
          </li>
        ) : null}
        {/* Sawyer 15 (E2) — the server refused (409) though this page saw
            everything approved: an approval changed since the page loaded. */}
        {openEntryRows.length === 0 && unapprovedRows.length === 0 ? (
          <li data-testid="export-warning-stale">
            The server says some timesheets in this period aren’t approved
            anymore. Reload to see the latest, or export anyway.
          </li>
        ) : null}
      </ul>
      <div className="ferro-ts-modal__actions">
        <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onCancel}>
          Go back
        </button>
        <button
          type="button"
          className="ferro-btn ferro-btn--primary"
          onClick={onConfirm}
          data-testid="export-confirm"
        >
          Export anyway
        </button>
      </div>
    </Modal>
  );
}

// ── Export history ─────────────────────────────────────────────────────

interface ExportHistoryProps {
  history: PayrollExportSummary[] | 'loading' | 'error';
  onRetry: () => void;
}

function fmtExportDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function ExportHistory({ history, onRetry }: ExportHistoryProps): JSX.Element {
  return (
    <section className="ferro-ts-history" aria-label="Export history">
      <h2 className="ferro-ts-history__title">Export history</h2>
      {history === 'loading' ? (
        <div className="ferro-skeleton ferro-skeleton--text" data-testid="history-skeleton" />
      ) : history === 'error' ? (
        <div className="ferro-alert ferro-alert--error" role="alert">
          <span>We couldn’t load past exports.</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline"
            onClick={onRetry}
          >
            Try again
          </button>
        </div>
      ) : history.length === 0 ? (
        <p className="ferro-muted" data-testid="history-empty">
          No payroll exports yet. Each export you run is saved here with a
          checksum, frozen even if a punch is corrected later.
        </p>
      ) : (
        <ul className="ferro-ts-history__list" data-testid="history-list">
          {history.map((e) => (
            <li key={e.id} className="ferro-ts-history__item">
              <span className="ferro-ts-history__file">{e.file_name}</span>
              {/* Sawyer 15 (E2) — which file; an older row reads Per period. */}
              <span className="ferro-ts-chip" data-testid={`history-format-${e.id}`}>
                {e.format === 'csv_workweek' ? 'Week-by-week' : 'Per period'}
              </span>
              <span className="ferro-ts-history__meta ferro-ts-tnum">
                {e.period_start} – {e.period_end} · {fmtExportDate(e.created_at)} ·
                checksum {e.checksum_sha256.slice(0, 12)}…
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export default Timesheets;
